import { isServer } from './common';
import {
  EXPERIMENT_NAME,
  SELECTED_VARIANT
} from './constants/localStorageConstants';

// To Check if local storage is compatible with the browser
export const checkLocalStorageCompatibility = () => {
  const result = typeof Storage !== 'undefined';
  return result;
};

export const checkNavigatorCompatibility = () => {
  return typeof Navigator !== 'undefined';
};

// To Check if cookies are enabled
export const checkCookiesEnabled = () => {
  return navigator && navigator.cookieEnabled;
};

// To check if internet is connected
export const checkInternetSupport = () => {
  return navigator && navigator.onLine;
};

export function isIos() {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

// To get language
export const getLanguage = () => {
  return navigator && navigator.language;
};

// To get app code name
export const getAppCodeName = () => {
  return navigator && navigator.appCodeName;
};

// To get app name
export const getAppName = () => {
  return navigator && navigator.appName;
};

// To get app version
export const getAppVersion = () => {
  return navigator && navigator.appVersion;
};

// To get platform
export const getPlatform = () => {
  return navigator && navigator.platform;
};

// To set value as cookie
export const setCookie = (
  key: string,
  value: string,
  days: any = undefined,
  time = 0,
  allowSubDomains = false
) => {
  if (!Number.isNaN(days)) {
    const date = new Date();
    // Get unix milliseconds at current time plus number of days
    if (!time) {
      date.setTime(+date + days * 86400000); // 24 * 60 * 60 * 1000
    } else if (!days && time) {
      date.setTime(date.getTime() + time * 60 * 1000); // current time + (time(variable) * 60 * 1000)
    } else {
      date.setTime(+date + days * 1000); // time * 1000 (mili seconds)
    }
    document.cookie = allowSubDomains
      ? `${key}=${value};expires=${date.toString()};path=/;domain=.zee5.com`
      : `${key}=${value};expires=${date.toString()};path=/`;
  } else {
    document.cookie = allowSubDomains
      ? `${key}=${value};path=/;domain=.zee5.com`
      : `${key}=${value};path=/`;
  }
};

// To get value from cookies
export const getCookie = (key: any) => {
  if (typeof document === 'undefined') return null;
  const value = document.cookie;
  let finalVal = null;
  if (document.cookie && document.cookie.indexOf(key.trim()) > -1) {
    const parts = value && value.split(';');
    const checkVal =
      parts && parts.filter((part) => part.indexOf(key.trim()) > -1);
    finalVal = checkVal && checkVal.length && checkVal[0].split('=')[1];
    return finalVal;
  }
  return finalVal;
};

// To Delete a Particular Cookie
export const deleteCookie = (key: any) => {
  if (document.cookie) {
    document.cookie = `${key}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    document.cookie = `${key}=; Path=/;domain=.zee5.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  }
};

// To set a value in Session storage
export const setSessionStorage = (key: string, value: unknown) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

// To get a value from Session storage
export const getSessionStorage = (key: string) => {
  let retrievedData;
  if (!isServer() && sessionStorage && sessionStorage.getItem(key)) {
    try {
      retrievedData = JSON.parse(sessionStorage.getItem(key) as string);
    } catch {
      retrievedData = sessionStorage.getItem(key);
    }
  }
  return retrievedData;
};

// To Remove Particular Session Storage Data
export const deleteSessionStorage = (key: any) => {
  if (sessionStorage && sessionStorage.getItem(key)) {
    sessionStorage.removeItem(key);
  }
};

// To set a value in local storage
export const setLocalStorage = (key: any, value: any) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (typeof window !== 'undefined' && !localStorage) {
    setCookie(key, value, null);
    return;
  }
  if (typeof value === 'string') {
    if (value) {
      localStorage.setItem(key, value);
    }
  } else {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

// To get a value from local storage
export const getLocalStorage = (key: any) => {
  let retrievedData: any;
  if (typeof window !== 'undefined' && !localStorage) {
    try {
      return JSON.parse(getCookie(key) as string);
    } catch (e) {
      return retrievedData;
    }
  }
  if (
    typeof window !== 'undefined' &&
    localStorage &&
    localStorage.getItem(key)
  ) {
    retrievedData = localStorage.getItem(key);
    if (typeof retrievedData === 'string') {
      return retrievedData;
    }
    return JSON.parse(retrievedData);
  }
  return retrievedData;
};

export const getLocalStorageInteger = (key: any) => {
  let retrievedData;
  if (localStorage && localStorage.getItem(key)) {
    retrievedData = localStorage.getItem(key);
    if (typeof retrievedData === 'string') {
      return parseInt(retrievedData, 10);
    }
    return retrievedData;
  }
  return retrievedData;
};

// To get an object from local storage
// Use this method to retrieve only objects from localStorage
export const getObjectFromLocalStorage = (key: any) => {
  let retrievedData: any;
  if (localStorage && localStorage.getItem(key)) {
    retrievedData = localStorage.getItem(key);
    retrievedData = JSON.parse(retrievedData);
    return retrievedData;
  }
  return retrievedData;
};

// To Remove Particular Local Storage Data
export const deleteLocalStorage = (key: any) => {
  if (localStorage && localStorage.getItem(key)) {
    localStorage.removeItem(key);
  }
};

// Check Platform name
export const checkPlatformName = () => {
  return navigator.userAgent;
};

export const getCookieData = (cname: any) => {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line prefer-template
    const name = cname + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }
  return null;
};

// get experiment name set in storage to be used by other events
export const getExperimentName = () => getSessionStorage(EXPERIMENT_NAME);

export const deleteExperimentName = () => deleteSessionStorage(EXPERIMENT_NAME);

export const setExperimentVariant = (variant: string) =>
  setLocalStorage(SELECTED_VARIANT, variant);

export const getExperimentVariant = () => getLocalStorage(SELECTED_VARIANT);

export const deleteExperimentVariant = () =>
  deleteLocalStorage(SELECTED_VARIANT);

// video analytics patches events store for mixpanel instrumentation
export const storeMixPanelMetricsInSS = (key: string, value: any) => {
  try {
    let mixpanelObj: any = {};
    if (window.sessionStorage.getItem('MIXPANEL_METRICS')) {
      mixpanelObj = JSON.parse(
        window.sessionStorage.getItem('MIXPANEL_METRICS') || '{}'
      );
    } else {
      window.sessionStorage.setItem('MIXPANEL_METRICS', '{}');
    }
    mixpanelObj[key] = value;
    window.sessionStorage.setItem(
      'MIXPANEL_METRICS',
      JSON.stringify(mixpanelObj)
    );
  } catch (error) {
    console.log('Error in Setting MIXPANEL_METRICS', error);
  }
};
